import { getGlobalHeadersAsObject } from "../global-headers";

export type ConnectionStatus =
	| "connected_via_invite"
	| "already_connected"
	| "awaiting_connection"
	| null;

export interface SentPitchDetail {
	/** Who wrote the pitch: the AI prompt version, else the chain step's
	 *  template, null when neither is on record (a hand-typed one-off). */
	template_name: string | null;
	message_type: string;
	sent_at: string;
	sent_by: string;
}

export interface ResponseDetail {
	text: string;
	response_type: string;
	received_at: string;
}

export interface VoterDetail {
	user_id: number;
	first_name: string;
	grade: string;
	created: string;
}

export interface ConnectedToDetail {
	name: string;
	connected_at: string | null;
}

export interface FunnelPerson {
	person_id: number;
	person_assignment_id: number | null;
	person_name: string;
	person_headline: string | null;
	person_url: string | null;
	vote_grade: string | null;
	vote_type: string | null;
	vote_created: string | null;
	is_upvote: boolean;
	is_queued: boolean;
	queued_at: string | null;
	queued_pitches: SentPitchDetail[];
	is_sent: boolean;
	sent_count: number;
	sent_pitches: SentPitchDetail[];
	has_response: boolean;
	response_detail: ResponseDetail | null;
	is_interested: boolean;
	is_interview: boolean;
	/** Came in through this assignment's public job ad, rather than sourced. */
	is_applicant: boolean;
	board_column: string | null;
	board_status: string | null;
	/**
	 * Set only when `board_column` above came from Jobylon. A Jobylon role still
	 * shows ODI's own column for anyone never sent there, so the value alone
	 * cannot tell the two apart.
	 */
	jobylon_stage: string | null;
	// Invite status fields
	invite_enqueued: boolean;
	invite_enqueued_at: string | null;
	invite_sent: boolean;
	invite_sent_at: string | null;
	is_connected: boolean;
	connection_status: ConnectionStatus;
	// Next action
	action: string | null;
	action_display: string | null;
	// Profile status
	is_unavailable: boolean;
	is_wrong_location: boolean;
	is_skipped: boolean;
	// User attribution
	voters: VoterDetail[];
	invite_queued_by: string[];
	invite_sent_by: string[];
	connected_to: ConnectedToDetail[];
}

export interface FunnelFilters {
	vote_grade?: string;
	is_upvote?: boolean;
	is_queued?: boolean;
	is_sent?: boolean;
	has_response?: boolean;
	is_interested?: boolean;
	is_interview?: boolean;
	is_applicant?: boolean;
}

export type LinkedInStatus = "ok" | "warning" | "error";

export interface SenderStatus {
	user_id: number;
	first_name: string;
	email: boolean;
	email_method: string | null;
	linkedin_status: LinkedInStatus;
	linkedin_last_ping: string | null;
	linkedin_reason_label: string | null;
	recruiter: boolean;
	sms: boolean;
}

export interface SenderStatusResponse {
	senders: SenderStatus[];
}

export const getAssignmentFunnelSenderStatus = async (
	assignmentId: number
): Promise<SenderStatusResponse> => {
	const url = `/api/v2/assignment/${assignmentId}/funnel/sender_status/`;
	const response = await fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (!response.ok) {
		throw new Error(`Failed to fetch sender status: ${response.statusText}`);
	}
	return response.json();
};

export interface RunMatrixRow {
	person_id: number;
	person_name: string | null;
	person_url: string | null;
	pitch_action: string | null;
	pitch_action_display: string | null;
	invite_action: string | null;
	invite_action_display: string | null;
}

export interface RunMatrixResponse {
	skipped: boolean;
	rows: RunMatrixRow[];
}

export interface TaskDispatchResponse {
	task_id: string;
}

export interface RunMatrixParams {
	perform?: boolean;
	resolve?: boolean;
	person_ids?: number[];
}

export const runAssignmentMatrix = async (
	assignmentId: number,
	params: RunMatrixParams = {}
): Promise<TaskDispatchResponse> => {
	const url = `/api/v2/assignment/${assignmentId}/funnel/run_matrix/`;
	const response = await fetch(url, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			perform: params.perform ?? true,
			resolve: params.resolve ?? true,
			person_ids: params.person_ids ?? [],
		}),
	});
	if (!response.ok) {
		throw new Error(`Failed to dispatch matrix task: ${response.statusText}`);
	}
	return response.json();
};

export interface ResolveJobResult {
	person_id: number;
	person_name: string;
	action: string;
	detail: string | null;
}

export interface RunRocketReachQueueResponse {
	locked: boolean;
	new_jobs_before: number;
	pending_jobs_before: number;
	results: ResolveJobResult[];
}

export const runRocketReachQueue = async (
	assignmentId: number
): Promise<TaskDispatchResponse> => {
	const url = `/api/v2/assignment/${assignmentId}/funnel/run_rocketreach_queue/`;
	const response = await fetch(url, {
		method: "POST",
		headers: getGlobalHeadersAsObject(),
	});
	if (!response.ok) {
		throw new Error(
			`Failed to dispatch RocketReach queue task: ${response.statusText}`
		);
	}
	return response.json();
};

export type CeleryTaskStatus = "PENDING" | "STARTED" | "RETRY" | "FAILURE" | "SUCCESS";

export interface TaskResultResponse<T = unknown> {
	id: number;
	task_id: string;
	task_name: string | null;
	status: CeleryTaskStatus;
	date_created: string;
	date_done: string | null;
	result: T | null;
	traceback: string | null;
}

export const getTaskResult = async <T = unknown>(
	taskId: string
): Promise<TaskResultResponse<T> | null> => {
	const url = `/api/v2/taskresult/${taskId}/`;
	const response = await fetch(url, { headers: getGlobalHeadersAsObject() });
	if (response.status === 404) return null;
	if (!response.ok) {
		throw new Error(`Failed to fetch task result: ${response.statusText}`);
	}
	return response.json();
};

export const getAssignmentFunnel = async (
	assignmentId: number,
	filters?: FunnelFilters
): Promise<FunnelPerson[]> => {
	const params = new URLSearchParams();

	if (filters) {
		if (filters.vote_grade !== undefined) {
			params.append("vote_grade", filters.vote_grade);
		}
		if (filters.is_upvote !== undefined) {
			params.append("is_upvote", String(filters.is_upvote));
		}
		if (filters.is_queued !== undefined) {
			params.append("is_queued", String(filters.is_queued));
		}
		if (filters.is_sent !== undefined) {
			params.append("is_sent", String(filters.is_sent));
		}
		if (filters.has_response !== undefined) {
			params.append("has_response", String(filters.has_response));
		}
		if (filters.is_interested !== undefined) {
			params.append("is_interested", String(filters.is_interested));
		}
		if (filters.is_interview !== undefined) {
			params.append("is_interview", String(filters.is_interview));
		}
		if (filters.is_applicant !== undefined) {
			params.append("is_applicant", String(filters.is_applicant));
		}
	}

	const queryString = params.toString();
	const url = `/api/v2/assignment/${assignmentId}/funnel/${queryString ? `?${queryString}` : ""}`;

	const response = await fetch(url, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch funnel data: ${response.statusText}`);
	}

	return response.json();
};
